import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";
import { addRejectedMatcher, handleAsyncThunkError } from "redux/reduxHelpers";
import { objectSerializer } from "helpers/utils";
import {
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper
} from "helpers/reducerHelpers";

const pifUrl = (projectId, buildingId, adaptationId, pifId) => {
  const projectUrl = `/crm/projects/${projectId}/buildings/${buildingId}/adaptations/${adaptationId}`
  return pifId ? `${projectUrl}/pifs/${pifId}` : `${projectUrl}/pifs`
}

const pifVersionUrl = (projectId, buildingId, changeOrderId, pifId) => {
  const projectUrl = `/crm/projects/${projectId}/buildings/${buildingId}/adaptations/${changeOrderId}`
  return pifId ? `${projectUrl}/pif_versions/${pifId}` : `${projectUrl}/pif_versions`
}

export const getPIF = createAsyncThunk(
  "PIF/getPIF",
  async ({projectId, buildingId, adaptationId, pifId}, { rejectWithValue }) => {
    try {
      const url = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(url)
      
      return response.data
    }
    catch(error){
      if (error.response && error.response.data) {
        return rejectWithValue(handleAsyncThunkError(error.response.data, 'Failed to get PIF'));
      }

      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get PIF'));
    }
  }
)

export const createPIF = createAsyncThunk(
  "PIF/createPIF",
  async ({projectId, buildingId, adaptationId, data}, { rejectWithValue }) => {
    try {
      const url = pifUrl(projectId, buildingId, adaptationId)
      const response = await apiAxios.post(url, {pif: data})
      return response.data
    }
    catch(error){
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create PIF'))
    }
  }
)

export const updatePIF = createAsyncThunk(
  "PIF/updatePIF",
  async ({ projectId, buildingId, adaptationId, pifId, data }, { rejectWithValue }) => {
    try {
      const url = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.put(url, {pif: data}, { timeout: 120000 })
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update PIF'));
    }
  }
)

export const deletePIF = createAsyncThunk(
  "PIF/deletePIF",
  async ({projectId, buildingId, adaptationId, pifId}, { rejectWithValue }) => {
    try {
      const url = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.delete(url);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to delete PIF'));
    }
  }
)

export const downloadProjectProposal = createAsyncThunk(
  "PIF/downloadProjectProposal",
  async ({projectId, buildingId, adaptationId, pifId, proposalType}, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(`${pifurl}/project_proposal?type=${proposalType}`, {responseType: 'blob'})
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Draft proposal contract ${proposalType}.docx`);
      document.body.appendChild(link);
      link.click();
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to download draft proposal contract'))
    }
  }
)

export const downloadPIF = createAsyncThunk(
  "PIF/downloadPIF",
  async ({projectId, buildingId, adaptationId, pifId, highlight, compareWith}, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const params = {}
      if (highlight) params.highlight = true
      if (compareWith) params.compare_with = compareWith
      const response = await apiAxios.get(`${pifurl}/export_pdf`, { params, responseType: 'blob' })
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'pif.pdf'; // Fallback filename
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+?)"/);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1]);
        }
      }

      // Create a link to download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to download PIF'))
    }
  }
)

export const downloadPIFAsJSON = createAsyncThunk(
  "PIF/downloadPIFAsJSON",
  async ({projectId, buildingId, adaptationId, pifId}, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(`${pifurl}/export_json`, {responseType: 'blob'})
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `pif-${pifId}.json`; // Fallback filename
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+?)"/);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1]);
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to download PIF as JSON'))
    }
  }
)

export const getPifDiff = createAsyncThunk(
  "PIF/getPifDiff",
  async ({ projectId, buildingId, adaptationId, pifId, against }, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(`${pifurl}/diff`, { params: against ? { against } : {} })
      return response.data
    }
    catch (error) {
      // No previous revision/CO to compare → not an error, just an empty diff.
      if (error.response?.status === 404) return null
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to compare PIF'))
    }
  }
)

// ── Restore from history ─────────────────────────────────────────────────────
// Every PIF save records the whole gds/areas jsonb, before and after, in the
// audit trail — so the history doubles as a set of restore points.

export const getPifRestorePoints = createAsyncThunk(
  "PIF/getPifRestorePoints",
  async ({ projectId, buildingId, adaptationId, pifId }, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(`${pifurl}/restore_points`)
      return response.data?.data || []
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to load restore points'))
    }
  }
)

// What restoring this point would change, against the PIF as it stands now.
export const getPifRestorePoint = createAsyncThunk(
  "PIF/getPifRestorePoint",
  async ({ projectId, buildingId, adaptationId, pifId, auditId, snapshot = 'before' }, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(`${pifurl}/restore_points/${auditId}`, { params: { snapshot } })
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to preview this restore point'))
    }
  }
)

export const restorePif = createAsyncThunk(
  "PIF/restorePif",
  async ({ projectId, buildingId, adaptationId, pifId, auditId, snapshot = 'before' }, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.post(`${pifurl}/restore`, { audit_id: auditId, snapshot })
      toast.success(response.data?.message || 'PIF was restored.')
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to restore the PIF'))
    }
  }
)

// Human-readable HTML summary of PIF changes vs the previous revision, used to
// pre-fill a revision's "List of Changes". 404 (no previous) → resolves to null.
export const getPifChangesSummary = createAsyncThunk(
  "PIF/getPifChangesSummary",
  async ({ projectId, buildingId, adaptationId, pifId, against }, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(`${pifurl}/changes_summary`, { params: against ? { against } : {} })
      return response.data
    }
    catch (error) {
      if (error.response?.status === 404) return null
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to generate summary'))
    }
  }
)

export const getDiffTargets = createAsyncThunk(
  "PIF/getDiffTargets",
  async ({ projectId, buildingId, adaptationId, pifId }, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(`${pifurl}/diff_targets`)
      return response.data
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to load compare targets'))
    }
  }
)



export const exportPifDiff = createAsyncThunk(
  "PIF/exportPifDiff",
  async ({ projectId, buildingId, adaptationId, pifId, against, format = 'xlsx' }, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(`${pifurl}/export_diff`, {
        params: { against, format },
        responseType: 'blob'
      })
      const contentDisposition = response.headers['content-disposition'];
      let filename = `pif-diff.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+?)"/);
        if (match && match[1]) filename = decodeURIComponent(match[1]);
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to export PIF changes'))
    }
  }
)

export const getActivityLog = createAsyncThunk(
  "PIF/getActivityLog",
  async ({projectId, buildingId, adaptationId, pifId, params}, { rejectWithValue }) => {
    try {
      const pifurl = pifUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(`${pifurl}/activity_log`, { params })
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get activity log'))
    }
  }
)

export const getBuildingsProjectCopyPIF = createAsyncThunk(
  "PIF/getBuildingsProjectCopyPIF",
  async ({projectId, params}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`/crm/projects/${projectId}/load_building_copy_pifs?type=${params}`)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get buildings project copy PIF'))
    }
  }
)

export const copyPIF = createAsyncThunk(
  "PIF/copyPIF",
  async ({ projectId, buildingId, adaptationId, data }, { rejectWithValue }) => {
    try {
      const url = pifUrl(projectId, buildingId, adaptationId)
      const response = await apiAxios.post(`${url}/copy_pif`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to copy PIF'))
    }
  }
)

export const getPIFVersion = createAsyncThunk(
  "PIF/getPIFVersion",
  async ({projectId, buildingId, adaptationId, pifId}, { rejectWithValue }) => {
    try {
      const url = pifVersionUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.get(url)
      
      return response.data
    }
    catch(error){
      if (error.response && error.response.data) {
        return rejectWithValue(handleAsyncThunkError(error, 'Failed to get PIF version'));
      }

      return rejectWithValue(handleAsyncThunkError(error, { message: error.message }));
    }
  }
)

export const updatePIFVersion = createAsyncThunk(
  "PIF/updatePIFVersion",
  async ({ projectId, buildingId, adaptationId, pifId, data }, { rejectWithValue }) => {
    try {
      const url = pifVersionUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.put(url, {pif: data})
      return response.data
    }
    catch(error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update PIF version'));
    }
  }
)

export const deletePIFVersion = createAsyncThunk(
  "PIF/deletePIFVersion",
  async ({projectId, buildingId, adaptationId, pifId}, { rejectWithValue }) => {
    try {
      const url = pifVersionUrl(projectId, buildingId, adaptationId, pifId)
      const response = await apiAxios.delete(url);
      return response.data
      
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to delete PIF version'));
    }
  }
)

const handlePayload = (state, action, type="") => {
  const { error, data, message } = action.payload;
  
  if (error !== undefined) {
    toast.error(error);
  } else {
    
    if (type === "delete") {
      state.pifData = {}
    }
    else {
      state.pifData = data.attributes
    }

    toast.success(message);
  }
};

const handleFulfilled = (state, action, dataKey) => {
  const results = action.payload || {};
  const data = state[dataKey];

  if (!data) {
    console.error(`❌ ERROR: state.${dataKey} does not exist!`);
    return;
  }

  // Extract rows from API response and deserialize JSONAPI attributes
  const rows = Array.isArray(results.rows) ? objectSerializer(results.rows) : [];
  console.log(`   Rows extracted:`, rows.length, "records");
  
  // Get current page from stored params (default to 1)
  const currentPage = data.currentParams?.page || 1;
  const pageSize = data.pageSize || 20;
  
  // Calculate pagination info
  const totalRows = Number.isFinite(results.total_rows) ? results.total_rows : 0;
  const totalPagesCount = Math.ceil(totalRows / pageSize) || 0;
  const fromRecord = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const toRecord = Math.min(currentPage * pageSize, totalRows);

  console.log(`   Pagination:`, { currentPage, totalRows, totalPagesCount, fromRecord, toRecord });

  // Update state with data
  data.data = rows;
  data.totalRows = totalRows;
  data.totalPagesCount = totalPagesCount;
  data.fromRecord = fromRecord;
  data.toRecord = toRecord;
  data.currentPage = currentPage;
  
  console.log(`✅ State updated. New data.data length:`, data.data.length);
};


const pifSlice = createSlice({
  name: "crm_project_pif",
  initialState: {
    pifData: {},
    areaData: [{d: 1, name: '1'}],
    collapseTabStatus: false,
    activityLogData: {
      data: [],
      totalRows: 0,
      pageSize: 20,
      totalPagesCount: 0,
      fromRecord: 0,
      toRecord: 0,
      currentParams: { page: 1, per_page: 20 },
      loading: false,
      error: null
    },
    initialKeys: {
      gds: [0],
      bds1: [0],
      bds2: [0]
    },
    diffData: null,
    diffTargets: [],
    diffLoading: false,
    diffExporting: false
  },
  reducers: {
    addAreaData: (state, action) => {
      const area = action.payload

      const checkArea = state.areaData.find(item => item.id === area.id)
      if (checkArea) {
        toast.error(`Area ${area.id} was existing.`)
      }
      else
      {
        state.areaData.push(area)
        toast.success(`Area ${area.id} was created.`)
      }
    },
    editAreaData: (state, action) => {
      const area = action.payload

      const updateIndex = state.areaData.findIndex(item => item.id === area.id)

      if (updateIndex !== -1)
      {
        state.areaData[updateIndex] = area
        toast.success(`Area ${area.id} was updated.`)
      } 
    },
    removeAreaData: (state, action) => {
      const area = action.payload
      const newData = state.areaData.filter(item => item.id !== area.id)
      state.areaData = newData 
    },
    resetAreaData: (state) => {
      state.areaData = [{d: 1, name: '1'}]
    },
    setExpandAll: (state) => {
      // Accordion eventKeys are 0-based ("0".."N") in both the form and the
      // read-only detail views, so ranges must start at 0 (previously "1".."N",
      // which left the first section — key "0" — collapsed). Ranges are generous;
      // keys with no matching section are simply ignored by the accordion.
      return {
        ...state,
        initialKeys: {
          ...state.initialKeys,
          gds: Array.from({ length: 20 }, (_, index) => String(index)),
          bds1: Array.from({ length: 13 }, (_, index) => String(index)),
          bds2: Array.from({ length: 11 }, (_, index) => String(index)),
        }
      }
    },
    resetExpandAll: (state) => {
      return {
        ...state,
        initialKeys: {
          ...state.initialKeys,
          gds: [],
          bds1: [],
          bds2: [],
        }
      };
    },
    setCollapseTabStatus: (state, action) => {
      state.collapseTabStatus = action.payload
    },
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPIF.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(createPIF.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(updatePIF.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(deletePIF.fulfilled, (state, action) => {
        handlePayload(state, action, "delete")
      })
      .addCase(downloadProjectProposal.pending, (state) => {
        state.exporting = true
      })
      .addCase(downloadProjectProposal.fulfilled, (state) => {
        state.exporting = false
        toast.success("Draft proposal contract has been downloaded successfully.")
      })
      .addCase(getPIFVersion.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(getPifDiff.pending, (state) => {
        state.diffLoading = true;
      })
      .addCase(getPifDiff.fulfilled, (state, action) => {
        state.diffLoading = false;
        state.diffData = action.payload;
      })
      .addCase(getPifDiff.rejected, (state) => {
        state.diffLoading = false;
        state.diffData = null;
      })
      .addCase(getDiffTargets.fulfilled, (state, action) => {
        state.diffTargets = action.payload?.data || [];
      })
      .addCase(exportPifDiff.pending, (state) => {
        state.diffExporting = true;
      })
      .addCase(exportPifDiff.fulfilled, (state) => {
        state.diffExporting = false;
        toast.success("PIF changes exported successfully.");
      })
      .addCase(exportPifDiff.rejected, (state) => {
        state.diffExporting = false;
      })
      .addCase(getActivityLog.pending, (state) => {
        state.activityLogData.loading = true;
        state.activityLogData.error = null;
      })
      .addCase(getActivityLog.fulfilled, (state, action) => {
        handleFulfilled(state, action, "activityLogData");
        state.activityLogData.loading = false;
      })
      .addCase(getActivityLog.rejected, (state, action) => {
        state.activityLogData.loading = false;
        state.activityLogData.error = action.payload?.error || "Failed to load activity log";
      })
      

      // Add standard rejected action matcher using shared helper
      addRejectedMatcher(builder, "PIF");
  }
})

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag,
  setExpandAll,
  resetExpandAll,
  setCollapseTabStatus
} = pifSlice.actions

export default pifSlice.reducer;